import React, { useState, useMemo } from 'react';
import { Package, Calendar, User, MapPin, FileText, Send, Container } from 'lucide-react';

const CollectedClientsTable = ({ 
  tasks, 
  onAssignToShipment, 
  onAssignToContainer,
  loading 
}) => {
  const [filterPeriod, setFilterPeriod] = useState('all'); // all, today, week, month
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter only completed tasks (collected clients)
  const collectedTasks = useMemo(() => {
    return tasks?.filter(task => ['collecte', 'depose', 'termine']?.includes(task?.status)) || [];
  }, [tasks]);

  // Apply period and search filters
  const filteredCollectedTasks = useMemo(() => {
    let filtered = collectedTasks;

    // Period filter
    if (filterPeriod === 'today') {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      filtered = filtered?.filter(task => task?.date === today);
    } else if (filterPeriod === 'week') {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo?.setDate(today?.getDate() - 7);
      filtered = filtered?.filter(task => {
        const taskDate = new Date(task?.date);
        return taskDate >= weekAgo && taskDate <= today;
      });
    } else if (filterPeriod === 'month') {
      const today = new Date();
      const monthAgo = new Date(today);
      monthAgo?.setMonth(today?.getMonth() - 1);
      filtered = filtered?.filter(task => {
        const taskDate = new Date(task?.date);
        return taskDate >= monthAgo && taskDate <= today;
      });
    } else if (filterPeriod === 'custom') {
      filtered = filtered?.filter(task => task?.date === selectedDate);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(task => 
        task?.clientName?.toLowerCase()?.includes(search) ||
        task?.clientPhone?.toLowerCase()?.includes(search) ||
        task?.clientAddress?.toLowerCase()?.includes(search) ||
        task?.clientCity?.toLowerCase()?.includes(search)
      );
    }

    return filtered;
  }, [collectedTasks, filterPeriod, selectedDate, searchTerm]);

  // Group by date for better organization
  const groupedByDate = useMemo(() => {
    const groups = {};
    filteredCollectedTasks?.forEach(task => {
      const date = task?.date;
      if (!groups?.[date]) {
        groups[date] = [];
      }
      groups?.[date]?.push(task);
    });
    return groups;
  }, [filteredCollectedTasks]);

  // Extract collected objects from notes
  const extractCollectedObjects = (notes) => {
    if (!notes) return 'Non spécifié';
    
    // Look for "Colis à collecter:" pattern
    const match = notes?.match(/Colis à collecter:\s*(.+?)(?:\n|$)/);
    if (match && match?.[1]) {
      return match?.[1]?.trim();
    }
    
    // If no pattern found, return first 50 chars of notes
    return notes?.substring(0, 50) + (notes?.length > 50 ? '...' : '');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des clients collectés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Package className="w-6 h-6 text-green-600" />
          Clients Collectés
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Liste des clients dont les colis ont été collectés - prêts pour assignation à un envoi ou conteneur
        </p>
      </div>
      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Period Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="w-4 h-4 inline mr-1" />
              Période
            </label>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="custom">Date personnalisée</option>
            </select>
          </div>

          {/* Custom Date Picker */}
          {filterPeriod === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date spécifique
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline mr-1" />
              Rechercher
            </label>
            <input
              type="text"
              placeholder="Nom, téléphone, adresse..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="text-gray-600">
            <strong className="text-gray-900">{filteredCollectedTasks?.length}</strong> client(s) collecté(s)
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600">
            <strong className="text-gray-900">{Object.keys(groupedByDate)?.length}</strong> date(s)
          </span>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        {filteredCollectedTasks?.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Aucun client collecté pour cette période</p>
            <p className="text-gray-400 text-sm mt-2">Les collectes terminées apparaîtront ici</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {Object.entries(groupedByDate)?.sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))?.map(([date, tasksForDate]) => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="px-6 py-3 bg-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700">
                      {formatDate(date)} ({tasksForDate?.length} collecte{tasksForDate?.length > 1 ? 's' : ''})
                    </h3>
                  </div>

                  {/* Tasks for this date */}
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Objets Collectés</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Envoi Lié</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tasksForDate?.map((task) => (
                        <tr key={task?.id} className="hover:bg-gray-50">
                          {/* Client Info */}
                          <td className="px-6 py-4">
                            <div className="flex items-start gap-2">
                              <User className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{task?.clientName}</div>
                                {task?.clientPhone && (
                                  <div className="text-sm text-gray-500">{task?.clientPhone}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Address */}
                          <td className="px-6 py-4">
                            <div className="flex items-start gap-2">
                              <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div>
                                <div className="text-sm text-gray-900">{task?.clientAddress || '-'}</div>
                                {task?.clientCity && (
                                  <div className="text-sm text-gray-500">{task?.clientCity}</div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Collected Objects */}
                          <td className="px-6 py-4">
                            <div className="flex items-start gap-2">
                              <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                              <div className="text-sm text-gray-700">
                                {extractCollectedObjects(task?.notes)}
                              </div>
                            </div>
                          </td>

                          {/* Linked Shipment */}
                          <td className="px-6 py-4">
                            {task?.shipmentId ? (
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                  ✓ Envoi créé
                                </span>
                                {task?.shipment?.reference && (
                                  <span className="text-xs text-gray-500">{task?.shipment?.reference}</span>
                                )}
                              </div>
                            ) : (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                                En attente
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {!task?.shipmentId ? (
                                <button
                                  onClick={() => onAssignToShipment(task)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                                  title="Créer un envoi pour ce client"
                                >
                                  <Send className="w-4 h-4" />
                                  Créer Envoi
                                </button>
                              ) : (
                                <button
                                  onClick={() => onAssignToContainer(task)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
                                  title="Assigner à un conteneur"
                                >
                                  <Container className="w-4 h-4" />
                                  Assigner Conteneur
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectedClientsTable;